// UtilityHitListPage.js — Admin dashboard for utility strike incidents
// Status lifecycle: Reported → Under Review → Repair Scheduled → Repaired → Closed
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, AlertTriangle, ChevronDown, ChevronUp, MapPin, Camera,
  RefreshCw, Loader, Calendar, Filter, Clock, CheckCircle, FileText
} from 'lucide-react';
import { colors, UTILITY_HIT_CONFIG } from '../config/constants';

const STATUS_COLORS = {
  'Reported': { bg: 'rgba(239,68,68,0.15)', text: '#ef4444' },
  'Under Review': { bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' },
  'Repair Scheduled': { bg: 'rgba(59,130,246,0.15)', text: '#3b82f6' },
  'Repaired': { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
  'Closed': { bg: 'rgba(107,114,128,0.15)', text: '#6b7280' },
};

const SEVERITY_COLORS = {
  minor: '#f59e0b',
  moderate: '#f97316',
  major: '#ef4444',
  critical: '#dc2626',
};

const UtilityHitListPage = ({ darkMode, setCurrentPage, loggedInUser }) => {
  const accentPrimary = darkMode ? '#667eea' : '#00b4d8';
  const bgColor = darkMode ? colors.dark : '#f8fafc';
  const cardBg = darkMode ? colors.darkLight : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [message, setMessage] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [updating, setUpdating] = useState(null);

  const fetchHits = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('project_id', 'BASTROP');
      if (statusFilter) params.set('status', statusFilter);

      const resp = await fetch(`/api/utility-hits?${params}`);
      const result = await resp.json();
      if (result.success) setHits(result.data || []);
    } catch (err) {
      console.error('Failed to fetch hits:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchHits(); }, [statusFilter]);

  const fetchDetail = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedData(null);
      return;
    }
    setExpandedId(id);
    try {
      const resp = await fetch(`/api/utility-hits?id=${id}`);
      const result = await resp.json();
      if (result.success) {
        setExpandedData({ hit: result.data, activity: result.activity || [] });
      }
    } catch (err) {
      console.error('Failed to fetch detail:', err);
    }
  };

  const updateStatus = async (id, newStatus) => {
    setUpdating(id);
    try {
      const resp = await fetch('/api/utility-hits', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id,
          field: 'status',
          value: newStatus,
          actor: loggedInUser?.name || loggedInUser?.email || 'Admin',
        }),
      });
      const result = await resp.json();
      if (result.success) {
        setMessage({ type: 'success', text: `Status updated to ${newStatus}` });
        setTimeout(() => setMessage(null), 2000);
        fetchHits();
        if (expandedId === id) fetchDetail(id);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update status' });
      setTimeout(() => setMessage(null), 3000);
    }
    setUpdating(null);
  };

  const parsePhotos = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch { return []; }
  };

  const statusFlow = UTILITY_HIT_CONFIG.statusFlow;

  const getNextStatus = (current) => {
    const idx = statusFlow.indexOf(current);
    return idx >= 0 && idx < statusFlow.length - 1 ? statusFlow[idx + 1] : null;
  };

  const inputStyle = {
    padding: '10px 12px',
    border: `1px solid ${borderColor}`,
    borderRadius: '8px',
    backgroundColor: darkMode ? '#1a2332' : '#ffffff',
    color: textColor,
    fontSize: '0.9rem',
    outline: 'none',
    appearance: 'auto',
    boxSizing: 'border-box',
  };

  // Summary stats
  const countByStatus = (s) => hits.filter(h => h.status === s).length;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, padding: '24px 16px' }}>
      {message && (
        <div style={{
          position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
          padding: '14px 28px', backgroundColor: message.type === 'success' ? '#28a745' : '#ef4444',
          color: '#fff', borderRadius: '10px', zIndex: 1000, fontSize: '0.95rem', fontWeight: '500',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          {message.text}
        </div>
      )}

      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <button onClick={() => setCurrentPage('admin-dashboard')} style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
            backgroundColor: 'transparent', border: `1px solid ${borderColor}`,
            borderRadius: '8px', color: textColor, cursor: 'pointer', fontSize: '0.9rem',
          }}>
            <ArrowLeft size={18} /> Back
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ef4444', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertTriangle size={24} />
              Utility Strike Tracker
            </h1>
            <p style={{ color: mutedColor, margin: '4px 0 0', fontSize: '0.85rem' }}>
              {hits.length} incident{hits.length !== 1 ? 's' : ''} total
            </p>
          </div>
          <button onClick={() => setCurrentPage('utility-hit')} style={{
            padding: '10px 18px', backgroundColor: '#ef4444', color: '#fff',
            border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600',
          }}>
            + New Report
          </button>
          <button onClick={fetchHits} disabled={loading} style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px',
            backgroundColor: accentPrimary, color: '#fff', border: 'none',
            borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem',
          }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          {statusFlow.map(s => {
            const sc = STATUS_COLORS[s];
            const count = countByStatus(s);
            return (
              <div
                key={s}
                onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
                style={{
                  padding: '12px', borderRadius: '10px', textAlign: 'center', cursor: 'pointer',
                  backgroundColor: statusFilter === s ? sc.bg : cardBg,
                  border: `1px solid ${statusFilter === s ? sc.text : borderColor}`,
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ fontSize: '1.4rem', fontWeight: '700', color: sc.text }}>{count}</div>
                <div style={{ fontSize: '0.75rem', color: mutedColor, marginTop: '2px' }}>{s}</div>
              </div>
            );
          })}
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: mutedColor }}>
            <Loader size={32} className="spin" style={{ marginBottom: '12px' }} />
            <p>Loading incidents...</p>
          </div>
        ) : hits.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: mutedColor }}>
            <AlertTriangle size={40} style={{ marginBottom: '12px', opacity: 0.3 }} />
            <p>No utility strikes recorded {statusFilter ? `with status "${statusFilter}"` : ''}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {hits.map((hit) => {
              const isExpanded = expandedId === hit.id;
              const sevColor = SEVERITY_COLORS[hit.severity] || '#6b7280';
              const sc = STATUS_COLORS[hit.status] || STATUS_COLORS['Reported'];
              const nextStatus = getNextStatus(hit.status);

              return (
                <div key={hit.id} style={{
                  backgroundColor: cardBg, borderRadius: '10px',
                  border: `1px solid ${isExpanded ? sevColor : borderColor}`,
                  overflow: 'hidden', transition: 'border-color 0.2s',
                }}>
                  {/* Row */}
                  <div
                    onClick={() => fetchDetail(hit.id)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '50px 90px 1fr 100px 90px 110px 24px',
                      gap: '10px', padding: '14px 16px', cursor: 'pointer', alignItems: 'center',
                    }}
                  >
                    <span style={{
                      fontWeight: '700', fontSize: '0.8rem', color: '#fff',
                      backgroundColor: sevColor, borderRadius: '6px', padding: '4px 0',
                      textAlign: 'center', textTransform: 'uppercase',
                    }}>
                      {(hit.severity || '?').slice(0, 3)}
                    </span>
                    <span style={{ color: textColor, fontSize: '0.85rem' }}>
                      {new Date(hit.incident_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span style={{ color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {hit.address}
                    </span>
                    <span style={{ color: mutedColor, fontSize: '0.85rem' }}>{hit.utility_type}</span>
                    <span style={{ color: mutedColor, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {hit.crew_lead_name || '—'}
                    </span>
                    <span style={{
                      padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600',
                      textAlign: 'center', backgroundColor: sc.bg, color: sc.text,
                    }}>
                      {hit.status}
                    </span>
                    {isExpanded ? <ChevronUp size={16} color={mutedColor} /> : <ChevronDown size={16} color={mutedColor} />}
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && expandedData && (
                    <div style={{
                      padding: '20px', borderTop: `1px solid ${borderColor}`,
                      backgroundColor: darkMode ? '#0a1425' : '#f9fafb',
                    }}>
                      {/* All 11 fields */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                        {[
                          { label: 'Date/Time', value: new Date(expandedData.hit.incident_date).toLocaleString() },
                          { label: 'Address', value: expandedData.hit.address },
                          { label: 'Utility Type', value: expandedData.hit.utility_type },
                          { label: 'Utility Owner', value: expandedData.hit.utility_owner || 'N/A' },
                          { label: 'Severity', value: expandedData.hit.severity, color: SEVERITY_COLORS[expandedData.hit.severity] },
                          { label: 'Marked Properly', value: expandedData.hit.marked_properly || 'N/A' },
                          { label: 'Locates Called', value: `${expandedData.hit.locates_called || 'N/A'}${expandedData.hit.locate_ticket_number ? ` (#${expandedData.hit.locate_ticket_number})` : ''}` },
                          { label: 'Crew Lead', value: `${expandedData.hit.crew_lead_name || 'N/A'} ${expandedData.hit.crew_lead_phone ? `(${expandedData.hit.crew_lead_phone})` : ''}` },
                          { label: 'Equipment', value: expandedData.hit.equipment_used || 'N/A' },
                          { label: 'Utility Rep', value: expandedData.hit.utility_rep_name ? `${expandedData.hit.utility_rep_name} (${expandedData.hit.utility_rep_contact || ''})` : 'None' },
                          { label: 'Reported By', value: `${expandedData.hit.reported_by} (${expandedData.hit.reported_by_email})` },
                          { label: 'GPS', value: expandedData.hit.gps_lat ? `${Number(expandedData.hit.gps_lat).toFixed(5)}, ${Number(expandedData.hit.gps_lng).toFixed(5)}` : 'N/A' },
                        ].map((f, i) => (
                          <div key={i}>
                            <div style={{ color: mutedColor, fontSize: '0.8rem', marginBottom: '2px' }}>{f.label}</div>
                            <div style={{ color: f.color || textColor, fontSize: '0.9rem', fontWeight: f.color ? '600' : '400' }}>{f.value}</div>
                          </div>
                        ))}
                      </div>

                      {/* Description */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ color: mutedColor, fontSize: '0.8rem', marginBottom: '4px', fontWeight: '600' }}>Description</div>
                        <div style={{
                          padding: '12px', backgroundColor: darkMode ? '#131f30' : '#fff',
                          borderRadius: '8px', border: `1px solid ${borderColor}`, color: textColor, fontSize: '0.9rem',
                        }}>
                          {expandedData.hit.description}
                        </div>
                      </div>

                      {/* Immediate Actions */}
                      {expandedData.hit.immediate_actions && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ color: mutedColor, fontSize: '0.8rem', marginBottom: '4px', fontWeight: '600' }}>Immediate Actions</div>
                          <div style={{
                            padding: '12px', backgroundColor: darkMode ? '#131f30' : '#fff',
                            borderRadius: '8px', border: `1px solid ${borderColor}`, color: textColor, fontSize: '0.9rem',
                          }}>
                            {expandedData.hit.immediate_actions}
                          </div>
                        </div>
                      )}

                      {/* Photos */}
                      {['photos_pre', 'photos_damage', 'photos_repair'].map(key => {
                        const photos = parsePhotos(expandedData.hit[key]);
                        if (photos.length === 0) return null;
                        const labels = { photos_pre: 'Pre-Construction', photos_damage: 'Damage', photos_repair: 'Repair' };
                        return (
                          <div key={key} style={{ marginBottom: '12px' }}>
                            <div style={{ color: mutedColor, fontSize: '0.8rem', marginBottom: '6px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Camera size={14} /> {labels[key]} Photos ({photos.length})
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {photos.map((url, pi) => (
                                <a key={pi} href={url} target="_blank" rel="noopener noreferrer">
                                  <img src={url} alt="" style={{
                                    width: '80px', height: '80px', objectFit: 'cover',
                                    borderRadius: '6px', border: `1px solid ${borderColor}`,
                                  }} />
                                </a>
                              ))}
                            </div>
                          </div>
                        );
                      })}

                      {/* Email status */}
                      {expandedData.hit.email_sent_at && (
                        <div style={{ marginBottom: '16px', padding: '8px 12px', backgroundColor: 'rgba(40,167,69,0.1)', borderRadius: '6px', fontSize: '0.85rem', color: '#28a745' }}>
                          Report emailed to Aspen on {new Date(expandedData.hit.email_sent_at).toLocaleString()}
                        </div>
                      )}

                      {/* Resolution notes */}
                      {expandedData.hit.resolution_notes && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ color: mutedColor, fontSize: '0.8rem', marginBottom: '4px', fontWeight: '600' }}>Resolution Notes</div>
                          <div style={{
                            padding: '12px', backgroundColor: darkMode ? '#131f30' : '#fff',
                            borderRadius: '8px', border: `1px solid ${borderColor}`, color: textColor,
                          }}>
                            {expandedData.hit.resolution_notes}
                          </div>
                        </div>
                      )}

                      {/* Activity log */}
                      {expandedData.activity.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ color: mutedColor, fontSize: '0.8rem', marginBottom: '8px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={14} /> Activity Log
                          </div>
                          {expandedData.activity.map((a, i) => (
                            <div key={i} style={{
                              display: 'flex', gap: '8px', alignItems: 'flex-start',
                              padding: '6px 0', borderBottom: i < expandedData.activity.length - 1 ? `1px solid ${borderColor}` : 'none',
                            }}>
                              <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: accentPrimary, marginTop: '6px', flexShrink: 0 }} />
                              <div>
                                <span style={{ color: textColor, fontSize: '0.85rem', fontWeight: '500' }}>{a.action}</span>
                                <span style={{ color: mutedColor, fontSize: '0.8rem' }}> — {a.actor || 'System'}</span>
                                <div style={{ color: mutedColor, fontSize: '0.75rem' }}>{new Date(a.created_at).toLocaleString()}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Status update action */}
                      {nextStatus && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                          <button
                            onClick={() => updateStatus(hit.id, nextStatus)}
                            disabled={updating === hit.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px',
                              padding: '10px 20px',
                              backgroundColor: STATUS_COLORS[nextStatus]?.text || accentPrimary,
                              color: '#fff', border: 'none', borderRadius: '8px',
                              cursor: updating === hit.id ? 'wait' : 'pointer',
                              fontSize: '0.9rem', fontWeight: '600',
                              opacity: updating === hit.id ? 0.7 : 1,
                            }}
                          >
                            {updating === hit.id ? <Loader size={14} className="spin" /> : <CheckCircle size={14} />}
                            Move to: {nextStatus}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
        @media (max-width: 768px) {
          div[style*="gridTemplateColumns: 50px 90px"] { grid-template-columns: 40px 70px 1fr 70px !important; }
          div[style*="gridTemplateColumns: 50px 90px"] > :nth-child(5),
          div[style*="gridTemplateColumns: 50px 90px"] > :nth-child(7) { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default UtilityHitListPage;
